import { Fragment } from "react";
import { Icon } from "../../kit";
import { useDomainState, useUi } from "../../../state/editor/useEditor";
import { BASIC_LEFT_RAIL, visibleLeftRail } from "./railConfig";
import { RAIL_W } from "../layout";
import { ScrollArea } from "../ScrollArea";

/** First-run coach marks: the three steps of a first picture. */
const COACH: Record<string, number> = { scenes: 1, metals: 2, output: 3 };

/**
 * The left rail. Every item carries its WORD — since the 2026-09-29 redesign
 * nothing on the rail is an icon you have to hover to decode.
 *
 * Basic mode shows the reference's preset panels (Metals · Gems · Ceramics ·
 * Pearls · Other · Scenes · Metal HDR · Gem HDR · Background · AI Match), a
 * divider, then Camera · Export · Share; Advanced shows the reference's
 * libraries. Clicking the active item collapses its panel; the chevron
 * re-opens the last one.
 *
 * Basic's thirteen items are sized to fit a 787px-tall window (the user's
 * 1512×787) without scrolling: 52px items, 46px under 900px of height. Below
 * that the list scrolls — in BOTH modes, through the shared `ScrollArea`, whose
 * fades show on whichever end has more — and the panel toggle stays put
 * underneath. (The fade used to be Basic-only, so Advanced's list at a short
 * window simply looked cut off.)
 */
export function LeftRail() {
  const [ui, patchUi] = useUi();
  const selection = useDomainState("selection");
  const basic = ui.inspectorMode === "basic";
  const items = basic ? BASIC_LEFT_RAIL : visibleLeftRail(!!selection, ui.showExperimental);
  const coach = basic && !ui.coachDone;

  const open = (id: string, active: boolean) =>
    patchUi(
      active
        ? { leftPanel: null, lastLeftPanel: id }
        : basic
          ? { leftPanel: id, lastBasicLeftPanel: id }
          : { leftPanel: id, lastAdvancedLeftPanel: id },
    );

  return (
    <nav
      aria-label="Studio panels"
      className="pointer-events-auto absolute left-0 top-0 bottom-0 z-20 flex flex-col border-r border-ed-border bg-ed-panel px-1 py-2.5"
      style={{ width: RAIL_W }}
    >
      <ScrollArea bar="none" className={`flex flex-col ${basic ? "gap-0.5" : "gap-1 [@media(max-height:860px)]:gap-0.5"}`}>
        {items.map((item) => {
          const active = ui.leftPanel === item.id;
          const step = coach ? COACH[item.id] : undefined;
          return (
            <Fragment key={item.id}>
              {"dividerBefore" in item && item.dividerBefore ? (
                <div className="mx-2 my-1 h-px shrink-0 bg-ed-border" role="separator" />
              ) : null}
              <button
                onClick={() => open(item.id, active)}
                aria-pressed={active}
                className={`relative flex w-full shrink-0 flex-col items-center rounded-[10px] px-0 transition-colors ${
                  basic
                    ? "min-h-[52px] gap-0.5 py-1.5 [@media(max-height:900px)]:min-h-[46px] [@media(max-height:900px)]:py-1"
                    : "min-h-[52px] gap-1 py-1.5 [@media(max-height:860px)]:min-h-[40px] [@media(max-height:860px)]:gap-0 [@media(max-height:860px)]:py-1"
                } ${active ? "bg-ed-active text-ed-text" : "text-ed-text-2 hover:bg-ed-hover hover:text-ed-text"}`}
                data-rail-item={item.id}
              >
                <Icon name={item.icon} size={20} />
                <span className={`w-full text-center text-[11px] leading-[13px] tracking-[-0.01em] [hyphens:auto] [overflow-wrap:anywhere] ${active ? "font-semibold" : "font-medium"}`}>
                  {item.label}
                </span>
                {step ? (
                  <span
                    className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-ed-ink text-[11px] font-semibold leading-none text-white"
                    aria-label={`Step ${step}`}
                  >
                    {step}
                  </span>
                ) : null}
              </button>
            </Fragment>
          );
        })}
      </ScrollArea>
      <button
        title={ui.leftPanel ? "Hide panel" : "Show panel"}
        aria-label={ui.leftPanel ? "Hide panel" : "Show panel"}
        onClick={() =>
          patchUi(
            ui.leftPanel
              ? { leftPanel: null, lastLeftPanel: ui.leftPanel }
              : { leftPanel: (basic ? ui.lastBasicLeftPanel : ui.lastAdvancedLeftPanel) ?? items[0].id },
          )
        }
        className="mt-1 flex h-9 w-full shrink-0 items-center justify-center rounded-lg text-ed-text-3 transition-colors hover:bg-ed-hover hover:text-ed-text"
      >
        <Icon name={ui.leftPanel ? "left_panel_close" : "left_panel_open"} size={20} />
      </button>
    </nav>
  );
}
